import { useEffect, useMemo, useState } from "react";

const PAGE_SIZE_OPTIONS = [25, 50, 100];

/**
 * Lightweight client-side pagination for any flat array of rows.
 *
 * Returns:
 *   page         : current 1-indexed page (resets to 1 if rows shrink past it)
 *   pageSize     : rows per page (default 25; choices 25/50/100)
 *   totalRows    : items in the source array
 *   totalPages   : ceil(totalRows / pageSize), min 1
 *   start, end   : 1-indexed inclusive window for the current page
 *   pageRows     : the actual slice to render
 *   setPage      : safe setter clamped to [1, totalPages]
 *   setPageSize  : setter; resets page → 1
 *   pageSizeOptions
 *
 * Usage stays purely client-side. No backend changes.
 */
export function usePagination(rows, { initialPageSize = 25 } = {}) {
  const [page, setPageRaw] = useState(1);
  const [pageSize, setPageSizeRaw] = useState(initialPageSize);

  const totalRows = Array.isArray(rows) ? rows.length : 0;
  const totalPages = Math.max(1, Math.ceil(totalRows / pageSize));

  // Guard: if the source shrinks (filter applied), drop back to last valid page.
  useEffect(() => {
    if (page > totalPages) setPageRaw(totalPages);
  }, [page, totalPages]);

  const pageRows = useMemo(() => {
    const startIdx = (page - 1) * pageSize;
    return (rows || []).slice(startIdx, startIdx + pageSize);
  }, [rows, page, pageSize]);

  const start = totalRows === 0 ? 0 : (page - 1) * pageSize + 1;
  const end = Math.min(page * pageSize, totalRows);

  const setPage = (p) => {
    const next = Math.max(1, Math.min(totalPages, Number(p) || 1));
    setPageRaw(next);
  };
  const setPageSize = (s) => {
    setPageSizeRaw(Number(s) || initialPageSize);
    setPageRaw(1);
  };

  return {
    page, pageSize, totalRows, totalPages,
    start, end, pageRows,
    setPage, setPageSize,
    pageSizeOptions: PAGE_SIZE_OPTIONS,
  };
}
